import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Loader2, RefreshCw, GitCommitHorizontal, CheckCircle2, AlertTriangle, Clock, Download, Github, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function CockpitUpdatePage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [history, setHistory] = useState([]);

  const loadStatus = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [sRes, hRes] = await Promise.all([
        api.get("/cockpit/update-status"),
        api.get("/cockpit/update-history").catch(() => ({ data: [] })),
      ]);
      setStatus(sRes.data);
      setHistory(Array.isArray(hRes.data) ? hRes.data : []);
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const runUpdate = async () => {
    if (!window.confirm("Appliquer la mise à jour maintenant ? Le backend redémarrera (~10 secondes d'indisponibilité).")) return;
    setUpdating(true);
    try {
      const { data } = await api.post("/cockpit/self-update");
      if (data.updated) {
        toast.success(`✅ Mise à jour ${data.before || "—"} → ${data.after} appliquée. Le backend redémarre.`);
        // Attendre le reboot puis recharger
        setTimeout(async () => {
          for (let i = 0; i < 10; i++) {
            await new Promise(r => setTimeout(r, 2000));
            try {
              await loadStatus(true);
              toast.info("Backend redémarré et opérationnel");
              break;
            } catch {}
          }
        }, 3000);
      } else {
        toast.info("Déjà à jour — aucune mise à jour appliquée.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-5xl" data-testid="cockpit-update-page">
      <header>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
          <GitCommitHorizontal className="w-3.5 h-3.5" /> COCKPIT · MISE À JOUR
        </div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Mise à jour du COCKPIT</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Synchronisez ce site avec la dernière version disponible sur GitHub. Le mécanisme télécharge l'archive depuis l'API GitHub puis remplace les fichiers (sauf <code className="font-mono bg-slate-100 px-1 py-0.5 rounded text-xs">.env</code> qui est protégé). Aucune donnée MongoDB n'est touchée.
        </p>
      </header>

      {/* État de configuration */}
      {!status?.configured && (
        <Card className="p-5 bg-amber-50 border-amber-200" data-testid="config-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-amber-900">Mise à jour non configurée</div>
              <div className="text-sm text-amber-800 mt-1">{status?.message}</div>
              <div className="text-xs text-amber-700 mt-3 space-y-1">
                <div className="font-bold uppercase tracking-wider text-[10px]">Procédure :</div>
                <div>1. Créer un GitHub Personal Access Token (fine-grained) sur <a href="https://github.com/settings/tokens?type=beta" target="_blank" rel="noopener noreferrer" className="underline">github.com/settings/tokens</a></div>
                <div>2. Repository access : <strong>cockpit-template-msc</strong> · Permissions : <strong>Contents read-only</strong></div>
                <div>3. Ajouter dans <code className="font-mono bg-white px-1 rounded">/app/backend/.env</code> :</div>
                <pre className="bg-white p-2 rounded text-[10px] mt-1 overflow-x-auto">{`GITHUB_TOKEN=ghp_xxxxxxxxxxxxxxxxxxxx
GITHUB_REPO=AgenceMSC/cockpit-template-msc
GITHUB_BRANCH=main`}</pre>
                <div>4. Redémarrer le backend : <code className="font-mono bg-white px-1 rounded text-[10px]">sudo supervisorctl restart backend</code></div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* État du repo */}
      {status?.configured && (
        <Card className="p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <Github className="w-5 h-5 text-slate-700" />
              <div>
                <div className="text-xs text-slate-500">Repo source</div>
                <a
                  href={`https://github.com/${status.repo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono font-bold text-slate-900 hover:text-[color:var(--brand-sky)]"
                >
                  {status.repo}
                </a>
                {status.branch && <span className="ml-2 text-xs text-slate-500">· branche {status.branch}</span>}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => loadStatus()} data-testid="refresh-status-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser
            </Button>
          </div>

          {!status.ok ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-red-900">Erreur GitHub</div>
                <div className="text-sm text-red-800">{status.message}</div>
              </div>
            </div>
          ) : (
            <div>
              {status.up_to_date ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3" data-testid="up-to-date">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  <div>
                    <div className="font-bold text-emerald-900">Vous êtes à jour</div>
                    <div className="text-xs text-emerald-700 mt-0.5">
                      Commit <code className="font-mono">{status.current.short}</code> · {status.latest.message}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="update-available">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                      <Download className="w-6 h-6 text-blue-600 shrink-0" />
                      <div>
                        <div className="font-bold text-blue-900">Mise à jour disponible</div>
                        <div className="text-xs text-blue-700 mt-0.5 font-mono flex items-center gap-1">
                          {status.current.short || <em className="text-blue-500">version locale inconnue</em>}
                          <ArrowRight className="w-3 h-3" />
                          {status.latest.short}
                        </div>
                        <div className="text-xs text-blue-800 mt-1 italic">{status.latest.message}</div>
                        {status.latest.date && (
                          <div className="text-[10px] text-blue-600 mt-0.5">
                            Publié le {new Date(status.latest.date).toLocaleString("fr-FR")}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={runUpdate}
                      disabled={updating}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="run-update-btn"
                    >
                      {updating ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Mise à jour…</>
                      ) : (
                        <><Download className="w-4 h-4 mr-1" /> Mettre à jour maintenant</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Détail */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Version locale</div>
                  {status.current.short ? (
                    <>
                      <div className="font-mono font-bold text-slate-900">{status.current.short}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{status.current.hash}</div>
                    </>
                  ) : (
                    <div className="text-xs text-slate-400 italic">Non détectée (premier déploiement)</div>
                  )}
                </div>
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Version distante</div>
                  <div className="font-mono font-bold text-slate-900">{status.latest.short}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{status.latest.hash}</div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Historique */}
      {history.length > 0 && (
        <Card className="p-5" data-testid="update-history">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Historique des mises à jour
          </h2>
          <div className="space-y-2">
            {history.slice(0, 10).map(log => (
              <div key={log.id} className="flex items-center gap-3 text-xs p-2 rounded bg-slate-50">
                <code className="font-mono">{(log.before || "—").slice(0, 7)}</code>
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <code className="font-mono font-bold">{(log.after || "").slice(0, 7)}</code>
                <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{log.files_updated} fichiers</Badge>
                {log.files_protected > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">{log.files_protected} protégés</Badge>
                )}
                <span className="text-slate-500 ml-auto">
                  {log.user_email} · {new Date(log.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Notes */}
      <Card className="p-4 bg-slate-50">
        <div className="text-xs text-slate-600">
          <div className="font-bold uppercase tracking-wider text-[10px] text-slate-700 mb-2">📌 Bon à savoir</div>
          <ul className="space-y-1">
            <li>· Le fichier <code className="font-mono bg-white px-1 rounded">backend/.env</code> et <code className="font-mono bg-white px-1 rounded">frontend/.env</code> sont <strong>toujours préservés</strong> (jamais écrasés).</li>
            <li>· Aucune donnée MongoDB n'est modifiée. Les utilisateurs, contenus, médias restent intacts.</li>
            <li>· Une mise à jour peut introduire de nouveaux champs DB — ils sont gérés idempotemment au boot.</li>
            <li>· En cas de problème, restaurez via la sauvegarde quotidienne automatique sur <strong>/admin/technical</strong>.</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}
